import { dungApiThat, goiApi } from '../api/http';
import type { ApiError } from '../errors';
import { mayChuCanhBao } from '../../mock/canh-bao';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  LocBanGhi,
  MaKenh,
  NotifyAlarmTask,
  NotifyAlarmTaskWrite,
  NotifyAlarmType,
  NotifyCatalog,
  NotifyCatalogPatch,
  NotifyChannel,
  NotifyChannelCreate,
  NotifyChannelType,
  NotifyChannelUpdate,
  NotifyDelivery,
  NotifyOverview,
  NotifyRecord,
  NotifyRecordDetail,
  NotifyTestResult,
  NotifyUsage,
  PhamViCanhBao,
  TelegramChat,
  TelegramChatsRequest,
  Trang,
} from '../../modules/canh-bao/types';

const GOC = '/api/v1/notify';
const GOC_NEN_TANG = '/api/v1/platform/notify';
const ma = encodeURIComponent;

/** Máy chủ giả chạy **sau** nhịp chờ và các trạng thái `?mock=`: lỗi tới cùng độ trễ với lỗi thật. */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

/** Trang danh sách phải có mảng `items`; thiếu là route chưa trỏ đúng service. */
function kiemTrang<T>(trang: Trang<T>): Trang<T> {
  if (!Array.isArray(trang?.items)) {
    throw { code: 'INTERNAL', status: 200, message: '' } satisfies ApiError;
  }
  return trang;
}

const RONG = { items: [], next_cursor: null };
const DANH_MUC_RONG: NotifyCatalog = { channel_types: [], alarm_types: [] };

/** `?mock=empty` trả rỗng; `?mock=filtered-empty` trả rỗng chỉ khi đang lọc (repository tự diễn, như khoá API). */
function danhSachGia<T>(lay: () => Trang<T>, dangLoc = false): Promise<Trang<T>> {
  if (dangLoc && mockState() === 'filtered-empty') return mockRequest<Trang<T>>(RONG);
  return mockRequest(undefined).then(() => (mockState() === 'empty' ? (RONG as Trang<T>) : lay()));
}

/**
 * Trung tâm thông báo (ADR-0102), qua Gateway tới notification-service. `/api/v1/notify/*` dùng chung cho hai cổng:
 * token tenant quản cấu hình của tenant trong token, token `PLATFORM_ADMIN` quản cấu hình của nền tảng — server phân
 * theo token, client không gửi `tenant_id`. `pv` chỉ để máy chủ giả biết đang diễn phạm vi nào.
 *
 * Ghi có điều kiện: `PUT` gửi `If-Match` là phiên bản đọc được (`"<version>"`). `POST` tạo gửi `Idempotency-Key`.
 */
export const canhBaoRepository = {
  danhMuc: (pv: PhamViCanhBao): Promise<NotifyCatalog> =>
    dungApiThat() ? goiApi<NotifyCatalog>(`${GOC}/catalog`) : mockRequest(mayChuCanhBao.danhMuc(pv), DANH_MUC_RONG),

  danhSachKenh: (pv: PhamViCanhBao, cursor?: string): Promise<Trang<NotifyChannel>> =>
    dungApiThat()
      ? goiApi<Trang<NotifyChannel>>(`${GOC}/channels`, {
          query: { limit: 100, cursor },
        }).then(kiemTrang)
      : danhSachGia(() => mayChuCanhBao.danhSachKenh(pv)),

  taoKenh: (pv: PhamViCanhBao, du: NotifyChannelCreate, khoaLap: string): Promise<NotifyChannel> =>
    dungApiThat()
      ? goiApi<NotifyChannel>(`${GOC}/channels`, {
          method: 'POST',
          body: du,
          idempotencyKey: khoaLap,
        })
      : giaLap(() => mayChuCanhBao.taoKenh(pv, du)),

  suaKenh: (pv: PhamViCanhBao, id: string, ifMatch: string, du: NotifyChannelUpdate): Promise<NotifyChannel> =>
    dungApiThat()
      ? goiApi<NotifyChannel>(`${GOC}/channels/${ma(id)}`, {
          method: 'PUT',
          body: du,
          ifMatch,
        })
      : giaLap(() => mayChuCanhBao.suaKenh(pv, id, ifMatch, du)),

  xoaKenh: (pv: PhamViCanhBao, id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`${GOC}/channels/${ma(id)}`, { method: 'DELETE' })
      : giaLap(() => mayChuCanhBao.xoaKenh(pv, id)),

  /** `POST …/channels/{id}/test` → `200` cả khi gửi hỏng (`ok: false`); `429` khi quá 5 lần mỗi 10 phút. */
  guiThu: (pv: PhamViCanhBao, id: string): Promise<NotifyTestResult> =>
    dungApiThat()
      ? goiApi<NotifyTestResult>(`${GOC}/channels/${ma(id)}/test`, {
          method: 'POST',
        })
      : giaLap(() => mayChuCanhBao.guiThu(pv, id)),

  chatTelegram: (du: TelegramChatsRequest): Promise<TelegramChat[]> =>
    dungApiThat()
      ? goiApi<{ items: TelegramChat[] }>(`${GOC}/telegram/chats`, {
          method: 'POST',
          body: du,
        }).then((r) => r.items ?? [])
      : giaLap(() => mayChuCanhBao.chatTelegram().items),

  danhSachTacVu: (pv: PhamViCanhBao, cursor?: string): Promise<Trang<NotifyAlarmTask>> =>
    dungApiThat()
      ? goiApi<Trang<NotifyAlarmTask>>(`${GOC}/alarm-tasks`, {
          query: { limit: 100, cursor },
        }).then(kiemTrang)
      : danhSachGia(() => mayChuCanhBao.danhSachTacVu(pv)),

  taoTacVu: (pv: PhamViCanhBao, du: NotifyAlarmTaskWrite, khoaLap: string): Promise<NotifyAlarmTask> =>
    dungApiThat()
      ? goiApi<NotifyAlarmTask>(`${GOC}/alarm-tasks`, {
          method: 'POST',
          body: du,
          idempotencyKey: khoaLap,
        })
      : giaLap(() => mayChuCanhBao.taoTacVu(pv, du)),

  suaTacVu: (pv: PhamViCanhBao, id: string, ifMatch: string, du: NotifyAlarmTaskWrite): Promise<NotifyAlarmTask> =>
    dungApiThat()
      ? goiApi<NotifyAlarmTask>(`${GOC}/alarm-tasks/${ma(id)}`, {
          method: 'PUT',
          body: du,
          ifMatch,
        })
      : giaLap(() => mayChuCanhBao.suaTacVu(pv, id, ifMatch, du)),

  xoaTacVu: (pv: PhamViCanhBao, id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(`${GOC}/alarm-tasks/${ma(id)}`, { method: 'DELETE' })
      : giaLap(() => mayChuCanhBao.xoaTacVu(pv, id)),

  /** `GET …/records` — mới nhất trước. `tenant_id`, `platform_only` chỉ Platform Admin dùng được. */
  danhSachBanGhi: (pv: PhamViCanhBao, loc: LocBanGhi, cursor?: string): Promise<Trang<NotifyRecord>> => {
    const dangLoc = Boolean(loc.task_id || loc.status || loc.tenant_id || loc.platform_only);
    return dungApiThat()
      ? goiApi<Trang<NotifyRecord>>(`${GOC}/records`, {
          query: {
            limit: 50,
            cursor,
            task_id: loc.task_id,
            status: loc.status,
            tenant_id: loc.tenant_id,
            platform_only: loc.platform_only ? 'true' : undefined,
          },
        }).then(kiemTrang)
      : danhSachGia(() => mayChuCanhBao.danhSachBanGhi(pv, loc), dangLoc);
  },

  chiTietBanGhi: (pv: PhamViCanhBao, id: string): Promise<NotifyRecordDetail> =>
    dungApiThat()
      ? goiApi<NotifyRecordDetail>(`${GOC}/records/${ma(id)}`)
      : giaLap(() => mayChuCanhBao.chiTietBanGhi(pv, id)),

  guiLai: (pv: PhamViCanhBao, deliveryId: string, khoaLap: string): Promise<NotifyDelivery> =>
    dungApiThat()
      ? goiApi<NotifyDelivery>(`${GOC}/deliveries/${ma(deliveryId)}/replay`, {
          method: 'POST',
          idempotencyKey: khoaLap,
        })
      : giaLap(() => mayChuCanhBao.guiLai(pv, deliveryId)),

  suDung: (): Promise<NotifyUsage> =>
    dungApiThat() ? goiApi<NotifyUsage>(`${GOC}/usage`) : mockRequest(mayChuCanhBao.suDung()),

  danhMucNenTang: (): Promise<NotifyCatalog> =>
    dungApiThat()
      ? goiApi<NotifyCatalog>(`${GOC_NEN_TANG}/catalog`)
      : mockRequest(mayChuCanhBao.danhMucNenTang(), DANH_MUC_RONG),

  suaLoaiKenh: (code: MaKenh, du: NotifyCatalogPatch): Promise<NotifyChannelType> =>
    dungApiThat()
      ? goiApi<NotifyChannelType>(`${GOC_NEN_TANG}/channel-types/${ma(code)}`, {
          method: 'PATCH',
          body: du,
        })
      : giaLap(() => mayChuCanhBao.suaLoaiKenh(code, du)),

  suaLoaiCanhBao: (code: string, du: NotifyCatalogPatch): Promise<NotifyAlarmType> =>
    dungApiThat()
      ? goiApi<NotifyAlarmType>(`${GOC_NEN_TANG}/alarm-types/${ma(code)}`, {
          method: 'PATCH',
          body: du,
        })
      : giaLap(() => mayChuCanhBao.suaLoaiCanhBao(code, du)),

  tongQuan: (): Promise<NotifyOverview> =>
    dungApiThat() ? goiApi<NotifyOverview>(`${GOC_NEN_TANG}/overview`) : mockRequest(mayChuCanhBao.tongQuan()),
};
